import React, { useState, useEffect } from "react"
import { graphql } from "gatsby"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import useProjectQuery from "../../../hooks/use-project-query"
import ProjectCard from "../../cards/project"
import { Logo, Search, Close, Expand, Collapse } from "../../icons"
import parse from "html-react-parser"
import StructuredText from "../custom-structured-text-renderer"

gsap.registerPlugin(ScrollTrigger)

const ProjectArchive = ({ title, richContent }) => {
  const { allDatoCmsProject, allDatoCmsSector, allDatoCmsServicesTag, allDatoCmsLocation } = useProjectQuery()

  const [searchTerm, setSearchTerm] = useState("")
  const [filter, setFilter] = useState(false)
  const [selectedSectors, setSelectedSectors] = useState([])
  const [selectedServices, setSelectedServices] = useState([])
  const [selectedLocations, setSelectedLocations] = useState([])
  const [filteredProjects, setFilteredProjects] = useState(allDatoCmsProject?.nodes || [])
  const [expandedFilters, setExpandedFilters] = useState([]) // Track multiple expanded filters

  // Sort sectors: move Other to bottom
  const sortedSectors = allDatoCmsSector?.nodes.sort((a, b) => {
    if (a.title === "Other") return 1
    if (b.title === "Other") return -1
    return 0
  })

  const toggleSelection = (selectedArray, setSelectedArray, item) => {
    if (selectedArray.includes(item)) {
      setSelectedArray(selectedArray.filter(selected => selected !== item))
    } else {
      setSelectedArray([...selectedArray, item])
    }
  }

  const toggleFilterExpand = (filterName) => {
    if (expandedFilters.includes(filterName)) {
      setExpandedFilters(expandedFilters.filter(name => name !== filterName)) // Collapse
    } else {
      setExpandedFilters([...expandedFilters, filterName]) // Expand
    }
  }

  // Apply filters based on query string
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const sector = params.get("sector")
    const servicesFromQuery = params.get("service")?.split(",") || [] // Allow multiple services

    if (sector) setSelectedSectors([sector])
    if (servicesFromQuery.length > 0) setSelectedServices(servicesFromQuery)

  }, [])

  useEffect(() => {
    let filtered = allDatoCmsProject?.nodes || []

    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.title.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (selectedSectors.length > 0) {
      filtered = filtered.filter(project =>
        project.sectors.some(sector => selectedSectors.includes(sector.title))
      )
    }

    if (selectedServices.length > 0) {
      filtered = filtered.filter(project =>
        project.servicesTags.some(tag => selectedServices.includes(tag.title))
      )
    }

    if (selectedLocations.length > 0) {
      filtered = filtered.filter(project =>
        project.locations.some(location => selectedLocations.includes(location.title))
      )
    }

    setFilteredProjects(filtered)
  }, [searchTerm, selectedSectors, selectedServices, selectedLocations, allDatoCmsProject?.nodes])

  const getHeadingText = () => {
    let heading = `${title}`
    
    const activeFilters = []
    if (selectedSectors.length > 0) activeFilters.push(selectedSectors.join(" / "))
    if (selectedServices.length > 0) activeFilters.push(selectedServices.join(" / "))
    if (selectedLocations.length > 0) activeFilters.push(selectedLocations.join(" / "))

    if (activeFilters.length > 0) {
      heading = `${filteredProjects.length} Results for<br />${activeFilters.join(" / ")}${searchTerm ? ` / ${searchTerm}"` : ""}`
    }

    if (activeFilters.length === 0 && searchTerm) {
      heading = `${filteredProjects.length} Results for<br />"${searchTerm}"`
    }

    return parse(heading)
  }

  const isFilterActive = () => {
    return searchTerm || selectedSectors.length > 0 || selectedServices.length > 0 || selectedLocations.length > 0
  }

  const clearFilters = () => {
    setSearchTerm("")
    setSelectedSectors([])
    setSelectedServices([])
    setSelectedLocations([])
  }

  return (
    <>
      <section className="title-content title-content--project">
        <div className="title-content__inner">
          <div className="title-content__wrapper">
            <div className="line line--vertical center-top"></div>
            <div className="title-content__title">
              <h1>{getHeadingText()}</h1>
            </div>
            <div className="title-content__content">
              {richContent && <StructuredText data={richContent} />}
            </div>
          </div>
        </div>
      </section>

      <section className="project-archive">
        <div className="project-archive__inner">
          <div className="project-archive__filter">
            {isFilterActive() ? (
              <button className="btn" onClick={clearFilters}>Clear Search <Close /></button>
            ) : (
              <button className="btn btn--orange" onClick={() => setFilter(!filter)}>Filter</button>
            )}
            <div className="line line--horizon center-left"></div>
            <div className="line line--horizon center-right"></div>
          </div>

          <div className="project-archive__wrapper">
            <div className="line line--vertical center-top"></div>
            <div className="line line--vertical center-bottom"></div>
            {filteredProjects.length > 0 && (
              <div className="project-archive__items">
                {filteredProjects.map((el, i) => (
                  <ProjectCard key={i} {...el} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className={`filter${filter ? " filter--active" : ""}`}>
        <div className="filter__inner">
          <div className="filter__header">
            <Logo />
            <button className="btn btn--border-white" onClick={() => setFilter(!filter)}>Close</button>
          </div>
          <form onSubmit={(e) => {e.preventDefault();setSearchTerm(searchTerm);setFilter(!filter)}} className="filter__search">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <button><Search /></button>
          </form>
          <div className="filter__items">
            <div className={`filter__item ${expandedFilters.includes("sector") ? "expanded" : ""}`}>
              <h4>
                <button onClick={() => toggleFilterExpand("sector")}>
                  by Sector <span>{expandedFilters.includes("sector") ? <Collapse /> : <Expand />}</span>
                </button>
              </h4>
              <ul>
                {sortedSectors?.map((el, i) => (
                  <li key={i}>
                    <button
                      onClick={() => toggleSelection(selectedSectors, setSelectedSectors, el.title)}
                      className={selectedSectors.includes(el.title) ? "active" : ""}
                    >
                      {el.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className={`filter__item ${expandedFilters.includes("service") ? "expanded" : ""}`}>
              <h4>
                <button onClick={() => toggleFilterExpand("service")}>
                  by Service <span>{expandedFilters.includes("service") ? <Collapse /> : <Expand />}</span>
                </button>
              </h4>
              <ul>
                {allDatoCmsServicesTag?.nodes.map((el, i) => (
                  <li key={i}>
                    <button
                      onClick={() => toggleSelection(selectedServices, setSelectedServices, el.title)}
                      className={selectedServices.includes(el.title) ? "active" : ""}
                    >
                      {el.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className={`filter__item ${expandedFilters.includes("location") ? "expanded" : ""}`}>
              <h4>
                <button onClick={() => toggleFilterExpand("location")}>
                  by Location <span>{expandedFilters.includes("location") ? <Collapse /> : <Expand />}</span>
                </button>
              </h4>
              <ul>
                {allDatoCmsLocation?.nodes.map((el, i) => (
                  <li key={i}>
                    <button
                      onClick={() => toggleSelection(selectedLocations, setSelectedLocations, el.title)}
                      className={selectedLocations.includes(el.title) ? "active" : ""}
                    >
                      {el.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <button className="filter__excess" onClick={() => setFilter(!filter)} />
      </section>
    </>
  )
}

export default ProjectArchive

export const query = graphql`
  fragment ProjectArchiveProps on DatoCmsProjectArchive {
    ... on DatoCmsProjectArchive {
      id
      title
      richContent {
        value
      }
    }
  }
`
